import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import {  cva } from 'class-variance-authority'
import type {VariantProps} from 'class-variance-authority';
import { cn } from '@/lib/utils'

const itemVariants = cva(
  'flex items-start gap-3 rounded-lg p-4 transition-colors',
  {
    variants: {
      variant: {
        default: 'bg-background',
        outline: 'border bg-card',
        muted: 'bg-muted/50',
      },
      size: {
        default: 'p-4',
        sm: 'p-3',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export interface ItemProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof itemVariants> {
  asChild?: boolean
}

const Item = React.forwardRef<HTMLDivElement, ItemProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'div'
    return (
      <Comp
        className={cn(itemVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  },
)
Item.displayName = 'Item'

const itemMediaVariants = cva('flex-shrink-0', {
  variants: {
    variant: {
      default: '',
      icon: 'flex items-center justify-center rounded-md bg-primary/10 p-2',
      image: 'overflow-hidden rounded-md',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

export interface ItemMediaProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof itemMediaVariants> {}

const ItemMedia = React.forwardRef<HTMLDivElement, ItemMediaProps>(
  ({ className, variant, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(itemMediaVariants({ variant, className }))}
        {...props}
      />
    )
  },
)
ItemMedia.displayName = 'ItemMedia'

const ItemContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('flex-1 space-y-1', className)} {...props} />
))
ItemContent.displayName = 'ItemContent'

const ItemTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn('font-semibold leading-none tracking-tight', className)}
    {...props}
  />
))
ItemTitle.displayName = 'ItemTitle'

const ItemDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
))
ItemDescription.displayName = 'ItemDescription'

const ItemActions = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex items-center gap-2 flex-shrink-0', className)}
    {...props}
  />
))
ItemActions.displayName = 'ItemActions'

const ItemHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('mb-4', className)} {...props} />
))
ItemHeader.displayName = 'ItemHeader'

const ItemFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('mt-4', className)} {...props} />
))
ItemFooter.displayName = 'ItemFooter'

const ItemGroup = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('space-y-2', className)} {...props} />
))
ItemGroup.displayName = 'ItemGroup'

const ItemSeparator = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('h-px bg-border my-2', className)}
    {...props}
  />
))
ItemSeparator.displayName = 'ItemSeparator'

export {
  Item,
  ItemMedia,
  ItemContent,
  ItemTitle,
  ItemDescription,
  ItemActions,
  ItemHeader,
  ItemFooter,
  ItemGroup,
  ItemSeparator,
}
